import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {TFunction} from 'i18next';
import {Moment} from 'moment';
import {User} from '../../../../backend/interfaces/user';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {pdfHeaderInfo} from '../base/export-pdf-header-info';
import {pdfFooterInfo} from '../base/export-pdf-footer-info';
import {renderCanvasToPdf} from '../base/export-pdf-render-canvas';
import {formatPdfDateRangeTitle, pdfAddBodyTitle} from '../base/export-pdf-footer-header-utils';
import {wait} from '../../../functions/wait';
import type {PreparedPdfExportFile} from '../base/export-data-types';
import {buildExportFileName} from '../base/export-file-name';

const PDF_MARGIN = 14;

type ShiftsAllocationsTablePDFParams = {
  title: string;
  startDate: Moment;
  endDate: Moment;
  user?: User;
  entity?: EntityInfo;
  t: TFunction;
};

export async function prepareShiftsAllocationsTablePDF({
  title,
  startDate,
  endDate,
  user,
  entity,
  t,
}: ShiftsAllocationsTablePDFParams): Promise<PreparedPdfExportFile> {
  try {
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    const reportDateRange = formatPdfDateRangeTitle({startDate, endDate});
    const cursorY = pdfAddBodyTitle(pdf, {
      title,
      subtitle: reportDateRange,
    });
    pdf.setProperties({title: `${title}: ${reportDateRange}`});

    await wait(250);

    const pageCount = await printShiftsAllocationsTableContent(
      pdf,
      'shifts-allocations-table-pdf',
      cursorY
    );

    pdf.setPage(1);
    pdfHeaderInfo(pdf, user, entity, t);

    for (let pageIndex = 1; pageIndex <= pageCount; pageIndex += 1) {
      pdf.setPage(pageIndex);
      pdfFooterInfo(pdf, `${pageIndex}`);
    }

    const fileName = buildExportFileName([entity?.shortName ?? entity?.name, title]);

    return {
      file: pdf,
      fileName,
    };
  } catch (error) {
    console.error('Error generating shifts allocations PDF:', error);
    throw new Error('Failed to generate PDF from shifts allocations table');
  }
}

async function printShiftsAllocationsTableContent(
  pdf: jsPDF,
  tableClassName: string,
  startPosY: number
): Promise<number> {
  const tableElement = document.getElementsByClassName(tableClassName)[0] as HTMLElement;

  if (!tableElement) {
    throw new Error('Shifts allocations table element not found');
  }

  const headCells = Array.from(tableElement.querySelectorAll('thead th')) as HTMLElement[];

  if (headCells.length === 0) {
    throw new Error('Shifts allocations table structure not found');
  }

  // Store original styles to restore later
  const originalTableElementStyle = tableElement.style.cssText;
  const originalHeadCellStyles = headCells.map((cell) => cell.style.cssText);

  const exportTableWidth = 118 + (headCells.length - 1) * 138 + 32;

  // Mutate styles for better PDF capture
  tableElement.style.position = 'relative';
  tableElement.style.zIndex = '-9999';
  tableElement.style.margin = '0';
  tableElement.style.width = `${exportTableWidth}px`;
  tableElement.style.maxWidth = 'none';
  tableElement.style.tableLayout = 'fixed';

  headCells.forEach((cell, index) => {
    cell.style.position = 'static';
    cell.style.top = 'auto';
    cell.style.paddingTop = '15px';
    cell.style.backgroundColor = '#ffffff';
    cell.style.minWidth = index === 0 ? '118px' : '138px';
    cell.style.width = index === 0 ? '118px' : '';
  });

  const fullScrollWidth = Math.ceil(tableElement.scrollWidth);
  const fullScrollHeight = Math.ceil(tableElement.scrollHeight);

  // wait a bit to ensure layout is updated.
  await wait(250);

  try {
    // Capture the table content as a canvas
    const canvas = await html2canvas(tableElement, {
      scale: 3, // Higher quality
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true,
      width: fullScrollWidth,
      height: fullScrollHeight,
      windowWidth: fullScrollWidth,
      windowHeight: fullScrollHeight,
      logging: false,
    });

    return renderCanvasToPdf({
      pdf,
      canvas,
      tableContentElement: tableElement,
      capturedHeight: fullScrollHeight,
      topMargin: startPosY,
      leftMargin: PDF_MARGIN,
      rightMargin: PDF_MARGIN,
      bottomMargin: PDF_MARGIN,
    });
  } finally {
    // Restore original styles
    tableElement.style.cssText = originalTableElementStyle;
    headCells.forEach((cell, index) => {
      cell.style.cssText = originalHeadCellStyles[index];
    });
  }
}
